import { CalculatorExpression, CALCULATOR_DISPLAY_SCALE } from '@st/util/calculator-expression'
import { Decimal, type Decimal as DecimalValue } from '@st/util/decimal'
import { CalculatorTapeAnnotation, CalculatorTapeLineItem } from '@st/sdk'
import { getCalculatorTapeLayout } from './calculator-tape-layout'

/**
 * Re-calculates totals and bounds.  Optionally strips empty line-items.
 */
export function rebuildCalculatorTape<T extends Omit<CalculatorTapeAnnotation, 'id' | 'time'>>(
  tape: T,
  opts?: { removeEmptyLineItems?: boolean }
): T {
  const sanitizedTape = opts?.removeEmptyLineItems
    ? {
        ...tape,
        lineItems: tape.lineItems.filter((lineItem) => lineItem.amount !== '')
      }
    : tape
  const layout = getCalculatorTapeLayout(sanitizedTape)
  const rowHeight = layout.row.height
  const { dividerHeight } = layout
  const containerPadding = layout.container.padding
  const contentRows = 1 + sanitizedTape.lineItems.length
  const width = layout.container.width
  const height =
    containerPadding.top +
    containerPadding.bottom +
    dividerHeight +
    rowHeight * contentRows +
    dividerHeight +
    rowHeight

  // Fast path: no items ➜ total 0.
  if (sanitizedTape.lineItems.length === 0) {
    return {
      ...sanitizedTape,
      bounds: { ...sanitizedTape.bounds, width, height },
      total: '0'
    } as T
  }

  const validAmounts = sanitizedTape.lineItems
    .map((item) => Decimal.parse(item.amount) ?? CalculatorExpression.evalExact(item.amount))
    .filter((amount): amount is DecimalValue => amount != undefined)

  const total =
    validAmounts.length == 0
      ? '0'
      : Decimal.formatFixed(
          validAmounts.reduce((sum, amount) => Decimal.add(sum, amount), Decimal.ZERO),
          CALCULATOR_DISPLAY_SCALE
        )

  return {
    ...sanitizedTape,
    bounds: { ...sanitizedTape.bounds, width, height },
    total
  } as T
}

/**
 * Appends a new line-item to the tape given free-form input.  The input may be
 * a lone expression (e.g. "19.95") or a description followed by an expression
 * (e.g. "USB Cable 19.95").
 */
export function addLineItemToTape(
  tape: CalculatorTapeAnnotation,
  inputStr: string
): CalculatorTapeAnnotation {
  const trimmed = inputStr.trim()
  const lastSpace = trimmed.lastIndexOf(' ')

  const description = lastSpace !== -1 ? trimmed.substring(0, lastSpace).trim() : ''
  const expr = lastSpace !== -1 ? trimmed.substring(lastSpace + 1).trim() : trimmed

  const result = CalculatorExpression.eval(expr)
  if (result === undefined) return tape // invalid expression – no-op

  const newItem: CalculatorTapeLineItem = { description, amount: result }

  return rebuildCalculatorTape({ ...tape, lineItems: [...tape.lineItems, newItem] })
}
